import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiTarget, FiUsers, FiCheckCircle, FiStar } = FiIcons;

const Formation = () => {
  const objectifs = [
    "Concevoir une action de formation : analyser les besoins, définir objectifs, déroulé et modalités",
    "Animer une session en présentiel/distanciel : pédagogie active, gestion de groupe, posture et communication",
    "Accompagner les apprenants : individualisation, suivi, remédiation et motivation",
    "Évaluer les acquis et la progression : outils d'évaluation, critères, traçabilité et bilan",
    "Structurer son activité de formateur : outils, méthodes, qualité (dont exigences type Qualiopi) et professionnalisation",
  ];

  const avantages = [
    {
      icon: FiCheckCircle,
      title: "Certification RNCP",
      description: "Titre reconnu au niveau national, apprécié des entreprises",
    },
    {
      icon: FiUsers,
      title: "Équipe experte",
      description:
        "Formateurs chevronnés et spécialistes siégeant en jury du Titre Professionnel",
    },
    {
      icon: FiTarget,
      title: "Offre modulaire",
      description: "Quatre parcours personnalisables selon votre situation",
    },
    {
      icon: FiStar,
      title: "Métier d'avenir",
      description:
        "Forte demande, débouchés variés et sécurisation de carrière",
    },
  ];

  return (
    <section id="formation" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Formateur pour adulte : un métier d'avenir !
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une opportunité professionnelle qui répond à une forte demande avec
            des débouchés variés et la possibilité de sécuriser une carrière
            professionnelle avec un titre reconnu.
          </p>
        </motion.div>

        {/* Avantages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {avantages.map((avantage, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow duration-300"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon
                  icon={avantage.icon}
                  className="h-8 w-8 text-blue-600"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {avantage.title}
              </h3>
              <p className="text-gray-600">{avantage.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Objectifs */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Objectifs de la formation
            </h3>
            <div className="space-y-4">
              {objectifs.map((objectif, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <SafeIcon
                    icon={FiCheckCircle}
                    className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-gray-700">{objectif}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              À qui s'adresse cette formation ?
            </h3>
            <div className="space-y-4 text-gray-700">
              <p>• Dirigeants d'organismes de formation</p>
              <p>• Professionnels souhaitant transmettre leur expertise</p>
              <p>• Formateurs internes occasionnels ou permanents</p>
              <p>• Tuteurs, animateurs, consultants, encadrants</p>
              <p>• Managers pratiquant l'accompagnement</p>
              <p>
                • Profils en transition professionnelle vers la formation
                d'adultes
              </p>
            </div>

            <div className="mt-8 p-6 bg-white rounded-xl">
              <h4 className="font-semibold text-gray-900 mb-2">
                Pourquoi choisir notre offre ?
              </h4>
              <p className="text-gray-600 text-sm">
                Notre offre pédagogique est modulaire et personnalisable, ce qui
                nous permet de vous proposer quatre parcours selon votre
                situation : la VAE, notre pack expert intégrant la certification
                « formateur pour adulte » et « prise de parole en public », une
                session intensive “Université d'été” de 102 heures et deux
                sessions de formation organisées en septembre et en janvier.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Formation;
